import { type PaletteOptions } from '@mui/material'
import { colors } from './colors'

declare module '@mui/material/styles' {
  interface TypeBackground {
    tinted: string
  }
}

// Tier colors are deliberately NOT a palette key — components read
// `colors.assessmentTier` directly.
declare module '@mui/material/styles' {
  interface Palette {
    // Outline of text fields, selects and filter chips. MUI has no slot for it.
    field: { border: string }
    heroOverlay: {
      scrim: string
      chipBg: string
      chipText: string
      titleShadow: string
    }
    // Keyed by PublishedProjectType so the marker can address them as `${type}.main`.
    unit: PaletteColor
    blueprint: PaletteColor
  }
  interface PaletteOptions {
    field?: { border: string }
    heroOverlay?: {
      scrim: string
      chipBg: string
      chipText: string
      titleShadow: string
    }
    unit?: SimplePaletteColorOptions
    blueprint?: SimplePaletteColorOptions
  }
  // Extra shades that pair with the standard light/main slots:
  //   primary.lightHover — hover for primary.light (secondary variant)
  //   primary.mainHover  — hover for primary.main  (primary contained button)
  interface PaletteColor {
    lightHover?: string
    mainHover?: string
  }
  interface SimplePaletteColorOptions {
    lightHover?: string
    mainHover?: string
  }
}

// Cloned from ./palette so the two designs can diverge without flag-checks in
// shared components. Only the redesign neutrals differ (secondary text, sidebar surfaces).
export const palette: PaletteOptions = {
  primary: {
    main: colors.teachTeal[800],
    mainHover: colors.teachTeal[900],
    dark: colors.teachTeal[900],
    light: colors.infoBlue.main,
    lightHover: colors.infoBlue.dark,
    contrastText: '#FFFFFF',
  },
  error: {
    contrastText: '#FFFFFF',
    main: '#D32F2F',
    light: '#EF5350',
  },
  // warning and info mains carry white text in filled components, so both clear 4.5:1 (ENG-1320).
  warning: {
    main: '#BF360C',
    dark: '#8F2808',
    light: '#FF5722',
    contrastText: '#FFFFFF',
  },
  info: {
    contrastText: '#FFFFFF',
    main: colors.infoBlue.main,
    dark: colors.infoBlue.dark,
  },
  success: {
    contrastText: '#FFFFFF',
    main: '#2E7D32',
    dark: '#1B5E20',
  },
  secondary: {
    main: 'rgba(0, 0, 0, 0.25)',
    light: 'rgba(0, 0, 0, 0.15)',
    dark: 'rgba(0, 0, 0, 0.4)',
    contrastText: '#FFFFFF',
  },
  action: {
    disabled: 'rgba(0, 0, 0, 0.38)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    hover: colors.redesignNeutral.hover,
    selected: colors.redesignNeutral.selected,
  },
  divider: 'rgba(0, 0, 0, 0.12)',
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: colors.redesignNeutral.secondaryText,
    disabled: colors.redesignNeutral.label,
  },
  background: {
    default: '#FFFFFF',
    tinted: colors.teachTeal[25],
  },
  field: {
    border: colors.redesignNeutral.fieldBorder,
  },
  heroOverlay: {
    scrim: colors.heroOverlay.scrim,
    chipBg: colors.heroOverlay.chipBg,
    chipText: colors.heroOverlay.chipText,
    titleShadow: colors.heroOverlay.titleShadow,
  },
  unit: { ...colors.dustyPlum, contrastText: '#FFFFFF' },
  blueprint: { ...colors.antiqueGold, contrastText: '#FFFFFF' },
}
